import React, { useState, useMemo } from 'react';
import styles from './GPACalculator.module.css';

const GRADE_OPTIONS = [
  { label: 'A+', value: 'A+', gpa: 4.0 },
  { label: 'A', value: 'A', gpa: 4.0 },
  { label: 'A-', value: 'A-', gpa: 3.7 },
  { label: 'B+', value: 'B+', gpa: 3.3 },
  { label: 'B', value: 'B', gpa: 3.0 },
  { label: 'B-', value: 'B-', gpa: 2.7 },
  { label: 'C+', value: 'C+', gpa: 2.3 },
  { label: 'C', value: 'C', gpa: 2.0 },
  { label: 'C-', value: 'C-', gpa: 1.7 },
  { label: 'D+', value: 'D+', gpa: 1.3 },
  { label: 'D', value: 'D', gpa: 1.0 },
  { label: 'D-', value: 'D-', gpa: 0.7 },
  { label: 'F', value: 'F', gpa: 0.0 },
];

const COURSE_LEVELS = [
  { label: 'Regular', value: 'regular' },
  { label: 'Honors', value: 'honors' },
  { label: 'AP/IB/College', value: 'ap' },
];

const CREDIT_OPTIONS = [5.0, 5.5, 6.0];

function getDefaultSemester(num) {
  return {
    name: `Semester ${num}`,
    courses: [],
    expanded: true,
  };
}

function getDefaultCourse() {
  return {
    name: '',
    grade: '',
    level: 'regular',
    credits: 5.0,
  };
}

function percentageToGPA(percentage) {
  const num = parseFloat(percentage);
  if (num >= 97) return 4.0;
  if (num >= 93) return 4.0;
  if (num >= 90) return 3.7;
  if (num >= 87) return 3.3;
  if (num >= 83) return 3.0;
  if (num >= 80) return 2.7;
  if (num >= 77) return 2.3;
  if (num >= 73) return 2.0;
  if (num >= 70) return 1.7;
  if (num >= 67) return 1.3;
  if (num >= 65) return 1.0;
  if (num >= 60) return 0.7;
  return 0.0;
}

function gpaToPercentage(gpa) {
  const num = parseFloat(gpa);
  if (num >= 3.85) return 95;
  if (num >= 3.7) return 92;
  if (num >= 3.5) return 88;
  if (num >= 3.3) return 85;
  if (num >= 3.0) return 82;
  if (num >= 2.7) return 78;
  if (num >= 2.3) return 75;
  if (num >= 2.0) return 72;
  if (num >= 1.7) return 68;
  if (num >= 1.3) return 65;
  if (num >= 1.0) return 62;
  if (num >= 0.7) return 60;
  return 55;
}

function getGradePoints(grade, level, mode) {
  let basePoints;
  
  if (mode === 'letter') {
    const selectedGrade = GRADE_OPTIONS.find(opt => opt.value === grade);
    if (selectedGrade) {
      basePoints = selectedGrade.gpa;
    } else {
      basePoints = 0; // Default for invalid grade
    }
  } else {
    basePoints = percentageToGPA(grade);
  }

  // Apply level modifiers
  if (level === 'honors') {
    basePoints = Math.min(basePoints + 0.5, 4.0);
  } else if (level === 'ap') {
    basePoints = Math.min(basePoints + 1.0, 4.0);
  }

  return basePoints;
}

function calculateGPA(courses, mode, weighted) {
  if (!courses.length) return 0;
  let totalPoints = 0;
  let totalCredits = 0;
  
  for (const course of courses) {
    if (!course.grade || !course.credits) continue;
    
    const credits = parseFloat(course.credits);
    const gradePoints = weighted 
      ? getGradePoints(course.grade, course.level, mode)
      : getGradePoints(course.grade, 'regular', mode);
    
    totalPoints += gradePoints * credits;
    totalCredits += credits;
  }
  
  if (totalCredits === 0) return 0;
  return totalPoints / totalCredits;
}

const GPACalculator = () => {
  const [semesters, setSemesters] = useState([getDefaultSemester(1)]);
  const [mode, setMode] = useState('letter'); // 'letter' or 'percentage'

  // GPA calculations
  const semesterGPAs = useMemo(() =>
    semesters.map(sem => ({
      weighted: calculateGPA(sem.courses, mode, true),
      unweighted: calculateGPA(sem.courses, mode, false),
    })),
    [semesters, mode]
  );
  
  const cumulative = useMemo(() => {
    const allCourses = semesters.flatMap(s => s.courses);
    return {
      weighted: calculateGPA(allCourses, mode, true),
      unweighted: calculateGPA(allCourses, mode, false),
    };
  }, [semesters, mode]);

  // Current semester (last semester) GPA
  const currentSemester = useMemo(() => {
    if (semesters.length === 0) return { weighted: 0, unweighted: 0 };
    const lastIndex = semesters.length - 1;
    return semesterGPAs[lastIndex] || { weighted: 0, unweighted: 0 };
  }, [semesterGPAs, semesters.length]);

  // Handlers
  const addSemester = () => {
    const newSemester = getDefaultSemester(semesters.length + 1);
    setSemesters([...semesters, newSemester]);
  };

  const removeSemester = idx => {
    if (semesters.length === 1) return;
    setSemesters(semesters.filter((_, i) => i !== idx));
  };

  const addCourse = idx => {
    setSemesters(semesters =>
      semesters.map((s, i) =>
        i === idx ? { ...s, courses: [...s.courses, getDefaultCourse()] } : s
      )
    );
  };

  const removeCourse = (semIdx, courseIdx) => {
    setSemesters(semesters =>
      semesters.map((s, i) =>
        i === semIdx
          ? { ...s, courses: s.courses.filter((_, j) => j !== courseIdx) }
          : s
      )
    );
  };

  const updateCourse = (semIdx, courseIdx, field, value) => {
    setSemesters(semesters =>
      semesters.map((s, i) =>
        i === semIdx
          ? {
              ...s,
              courses: s.courses.map((c, j) =>
                j === courseIdx ? { ...c, [field]: value } : c
              ),
            }
          : s
      )
    );
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>GPA Calculator</h1>
        <div className={styles.gradeToggle} data-mode={mode}>
          <button
            className={`${styles.toggleOption} ${mode === 'letter' ? styles.active : ''}`}
            onClick={() => {
              console.log('Letter button clicked');
              setMode('letter');
            }}
            type="button"
            aria-pressed={mode === 'letter'}
          >
            Letter Grades
          </button>
          <button
            className={`${styles.toggleOption} ${mode === 'percentage' ? styles.active : ''}`}
            onClick={() => {
              console.log('Percentage button clicked');
              setMode('percentage');
            }}
            type="button"
            aria-pressed={mode === 'percentage'}
          >
            Percentage
          </button>
        </div>
        <div className={styles.modeIndicator}>
          {mode === 'letter' ? 'Enter grades as A+, B-, etc.' : 'Enter grades as percentages (0-100)'}
        </div>
        <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '4px' }}>
          Current mode: <strong>{mode}</strong>
        </div>
      </header>

      <div className={styles.mainContent}>
        <div className={styles.semestersSection}>
          {semesters.length === 0 ? (
            <div className={styles.emptyState}>
              <h3>No semesters yet</h3>
              <p>Add your first semester to get started</p>
            </div>
          ) : (
            semesters.map((sem, semIdx) => (
              <div className={styles.semesterCard} key={semIdx}>
                <div className={styles.semesterHeader}>
                  <div>
                    <div className={styles.semesterTitle}>{sem.name}</div>
                    <div className={styles.semesterGpa}>
                      GPA: {semesterGPAs[semIdx].weighted.toFixed(2)}
                    </div>
                  </div>
                  {semesters.length > 1 && (
                    <button
                      className={styles.removeSemester}
                      onClick={() => removeSemester(semIdx)}
                      aria-label={`Remove ${sem.name}`}
                      type="button"
                    >
                      ×
                    </button>
                  )}
                </div>
                
                <table className={styles.coursesTable}>
                  <thead>
                    <tr>
                      <th>Course Name</th>
                      <th>Grade</th>
                      <th>Level</th>
                      <th>Credits</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sem.courses.length === 0 ? (
                      <tr>
                        <td colSpan={5} className={styles.emptyPrompt}>
                          Start by adding your first course
                        </td>
                      </tr>
                    ) : (
                      sem.courses.map((course, courseIdx) => (
                        <tr key={courseIdx}>
                          <td>
                            <input
                              type="text"
                              className={styles.courseInput}
                              placeholder="Course name"
                              value={course.name}
                              onChange={e => updateCourse(semIdx, courseIdx, 'name', e.target.value)}
                            />
                          </td>
                          <td>
                            {mode === 'letter' ? (
                              <select
                                className={styles.courseSelect}
                                value={course.grade}
                                onChange={e => updateCourse(semIdx, courseIdx, 'grade', e.target.value)}
                              >
                                <option value="">Select grade</option>
                                {GRADE_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="number"
                                className={styles.courseInput}
                                placeholder="0-100"
                                min="0"
                                max="100"
                                value={course.grade}
                                onChange={e => updateCourse(semIdx, courseIdx, 'grade', e.target.value)}
                              />
                            )}
                          </td>
                          <td>
                            <select
                              className={styles.courseSelect}
                              value={course.level}
                              onChange={e => updateCourse(semIdx, courseIdx, 'level', e.target.value)}
                            >
                              {COURSE_LEVELS.map(lvl => (
                                <option key={lvl.value} value={lvl.value}>{lvl.label}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              className={styles.courseSelect}
                              value={course.credits}
                              onChange={e => updateCourse(semIdx, courseIdx, 'credits', e.target.value)}
                            >
                              {CREDIT_OPTIONS.map(credit => (
                                <option key={credit} value={credit}>{credit}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <button
                              className={styles.removeCourse}
                              onClick={() => removeCourse(semIdx, courseIdx)}
                              aria-label="Remove Course"
                              type="button"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                
                <button
                  className={styles.addCourseBtn}
                  onClick={() => addCourse(semIdx)}
                  type="button"
                >
                  + Add Course
                </button>
              </div>
            ))
          )}
          
          <button className={styles.addSemesterBtn} onClick={addSemester} type="button">
            + Add Semester
          </button>
        </div>

        <aside className={styles.gpaSidebar}>
          <div className={styles.gpaSection}>
            <h3>Current Semester</h3>
            <div className={styles.gpaValue}>{currentSemester.unweighted.toFixed(2)}</div>
            <div className={styles.gpaLabel}>Unweighted GPA</div>
            <div className={styles.gpaValue}>{currentSemester.weighted.toFixed(2)}</div>
            <div className={styles.gpaLabel}>Weighted GPA</div>
          </div>
          
          <div className={`${styles.gpaSection} ${styles.cumulativeGpa}`}>
            <div className={styles.gpaValue}>{cumulative.weighted.toFixed(2)}</div>
            <div className={styles.gpaLabel}>Cumulative GPA</div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default GPACalculator; 